import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Dices as DicesIcon } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  parseSeedValues,
  RANGE_BOUNDS,
  countInRange,
  bestFitRangeKey,
} from "../lib/seedInput";

/**
 * Iter 77 — SeedInputPanel. Extracted from NotificationsPage so the
 * host page doesn't have to own the ~500 lines of markup + state that
 * back the Force-CTR Test Seeder input.
 *
 * The parent still owns the network call + history refetch — this
 * component just gathers user input and yields the parsed values via
 * ``onSeed(values | undefined)``. Passing ``undefined`` sends no body
 * so the backend uses its default set.
 *
 * State owned here:
 *   - seedValuesInput (raw + debounced mirror + elapsed tick)
 *   - seedRandomRange (persisted per-device)
 *   - legendChanged emphasis flags with a paired change timer
 *
 * All previously-shipped Iter 65-76 UX polish behaviours are preserved.
 */
export default function SeedInputPanel({ onSeed, random = Math.random }) {
  const [seedValuesInput, setSeedValuesInput] = useState("20,45,60");
  const [debouncedSeedValuesInput, setDebouncedSeedValuesInput] = useState("20,45,60");
  const [debounceElapsedMs, setDebounceElapsedMs] = useState(0);
  const [seedRandomRange, setSeedRandomRange] = useState(() => {
    try {
      const v = localStorage.getItem("stratos.seed-random-range");
      if (["spread", "low", "mid", "high"].includes(v)) return v;
    } catch {}
    return "spread";
  });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSeedValuesInput(seedValuesInput), 150);
    return () => clearTimeout(t);
  }, [seedValuesInput]);

  useEffect(() => {
    if (seedValuesInput === debouncedSeedValuesInput) {
      setDebounceElapsedMs(0);
      return undefined;
    }
    const start = performance.now();
    setDebounceElapsedMs(0);
    const iv = setInterval(() => {
      const elapsed = Math.min(150, Math.round(performance.now() - start));
      setDebounceElapsedMs(elapsed);
    }, 30);
    return () => clearInterval(iv);
  }, [seedValuesInput, debouncedSeedValuesInput]);

  const legendCounts = React.useMemo(() => {
    const nums = parseSeedValues(debouncedSeedValuesInput);
    if (!nums) return null;
    let ups = 0, downs = 0, flats = 0;
    nums.forEach((v, i) => {
      if (i === 0) { flats += 1; return; }
      const prev = nums[i - 1];
      if (v > prev) ups += 1;
      else if (v < prev) downs += 1;
      else flats += 1;
    });
    return { ups, downs, flats };
  }, [debouncedSeedValuesInput]);

  const prevLegendCountsRef = React.useRef({ ups: null, downs: null, flats: null });
  const [legendChanged, setLegendChanged] = useState({ ups: false, downs: false, flats: false });
  const legendChangeTimerRef = React.useRef(null);
  useEffect(() => {
    if (!legendCounts) {
      prevLegendCountsRef.current = { ups: null, downs: null, flats: null };
      return undefined;
    }
    const prev = prevLegendCountsRef.current;
    const changed = {
      ups: prev.ups !== null && prev.ups !== legendCounts.ups,
      downs: prev.downs !== null && prev.downs !== legendCounts.downs,
      flats: prev.flats !== null && prev.flats !== legendCounts.flats,
    };
    prevLegendCountsRef.current = { ...legendCounts };
    if (changed.ups || changed.downs || changed.flats) {
      setLegendChanged(changed);
      if (legendChangeTimerRef.current) clearTimeout(legendChangeTimerRef.current);
      legendChangeTimerRef.current = window.setTimeout(() => {
        setLegendChanged({ ups: false, downs: false, flats: false });
        legendChangeTimerRef.current = null;
      }, 800);
    }
    return undefined;
  }, [legendCounts]);

  const handleSeedClick = () => {
    const raw = (seedValuesInput || "").trim();
    if (!raw) {
      onSeed(undefined);
      return;
    }
    const parts = raw.split(",").map((s) => s.trim()).filter(Boolean);
    const nums = parts.map((p) => Number(p));
    if (nums.some((n) => !Number.isFinite(n) || n < 0 || n > 100)) {
      toast.error("Seed failed", {
        description: "Values must be numbers between 0 and 100 (comma-separated).",
      });
      return;
    }
    if (nums.length < 2 || nums.length > 10) {
      toast.error("Seed failed", {
        description: "Provide between 2 and 10 comma-separated values.",
      });
      return;
    }
    onSeed(nums);
  };

  const emphCls = (isOn) => isOn
    ? "text-zinc-100 font-bold tabular-nums"
    : "text-zinc-300 tabular-nums";

  const parsedNums = parseSeedValues(seedValuesInput);

  return (
    <div className="inline-flex flex-col items-start gap-0.5">
      <div className="inline-flex items-center gap-1">
        <input
          type="text"
          data-testid="seed-values-input"
          value={seedValuesInput}
          onChange={(e) => setSeedValuesInput(e.target.value)}
          placeholder="20,45,60"
          spellCheck={false}
          className="mono text-[9px] tabular-nums px-1.5 py-0.5 rounded-sm border border-zinc-700 bg-transparent text-zinc-300 w-24 focus:outline-none focus:border-amber-500/60"
          title="Comma-separated CTR values (0-100), 2-10 entries"
        />
        {seedValuesInput !== debouncedSeedValuesInput && (
          <span
            data-testid="seed-debounce-hint-chip"
            data-elapsed={debounceElapsedMs}
            className="mono text-[8px] uppercase tracking-widest text-amber-300/80 border border-amber-500/40 bg-amber-500/5 rounded-sm px-1 py-[1px] inline-flex items-center gap-1"
            title={`Legend counts + emphasis will refresh 150ms after you stop typing (${debounceElapsedMs}ms elapsed)`}
          >
            settling <span
              data-testid="seed-debounce-elapsed-value"
              className="tabular-nums text-amber-200"
            >
              {debounceElapsedMs}
            </span>ms
            <span
              key={seedValuesInput}
              data-testid="seed-debounce-progress-bar"
              data-elapsed={debounceElapsedMs}
              className="w-4 h-[2px] bg-amber-300/70 rounded-sm inline-block"
              aria-hidden="true"
              title={`${debounceElapsedMs}ms of 150ms`}
            />
          </span>
        )}
        <button
          type="button"
          data-testid="seed-randomize-btn"
          onClick={() => {
            const ranges = { spread: [0, 100], low: [0, 30], mid: [30, 70], high: [70, 100] };
            const [lo, hi] = ranges[seedRandomRange] || ranges.spread;
            const span = hi - lo;
            const rand = Array.from({ length: 4 }, () =>
              Math.floor(random() * (span + 1)) + lo,
            );
            setSeedValuesInput(rand.join(","));
          }}
          className="mono text-[9px] uppercase tracking-widest p-1 rounded-sm border border-zinc-700 bg-transparent text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200 transition-colors inline-flex items-center"
          title={`Randomize with 4 values (range: ${seedRandomRange})`}
        >
          <DicesIcon size={11} strokeWidth={2} />
        </button>
        <Select
          value={seedRandomRange}
          onValueChange={(v) => {
            setSeedRandomRange(v);
            try { localStorage.setItem("stratos.seed-random-range", v); } catch {}
          }}
        >
          <SelectTrigger
            data-testid="seed-range-select"
            className="h-6 w-20 mono text-[9px] uppercase tracking-widest border-zinc-700 bg-transparent text-zinc-300 px-1.5 py-0"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="spread" data-testid="seed-range-spread">
              <span className="inline-flex items-center gap-2">
                <span data-testid="seed-range-dots-spread" className="relative inline-block w-8 h-2" aria-hidden="true">
                  <span className="absolute w-1 h-1 rounded-full bg-zinc-400" style={{ left: "0%", top: 2 }} />
                  <span className="absolute w-1 h-1 rounded-full bg-zinc-400" style={{ left: "50%", top: 2, transform: "translateX(-50%)" }} />
                  <span className="absolute w-1 h-1 rounded-full bg-zinc-400" style={{ right: "0%", top: 2 }} />
                </span>
                Spread
              </span>
            </SelectItem>
            <SelectItem value="low" data-testid="seed-range-low">
              <span className="inline-flex items-center gap-2">
                <span data-testid="seed-range-dots-low" className="relative inline-block w-8 h-2" aria-hidden="true">
                  <span className="absolute w-1 h-1 rounded-full bg-red-400" style={{ left: "0%", top: 2 }} />
                  <span className="absolute w-1 h-1 rounded-full bg-red-400" style={{ left: "12%", top: 2 }} />
                  <span className="absolute w-1 h-1 rounded-full bg-red-400" style={{ left: "24%", top: 2 }} />
                </span>
                Low 0-30
              </span>
            </SelectItem>
            <SelectItem value="mid" data-testid="seed-range-mid">
              <span className="inline-flex items-center gap-2">
                <span data-testid="seed-range-dots-mid" className="relative inline-block w-8 h-2" aria-hidden="true">
                  <span className="absolute w-1 h-1 rounded-full bg-amber-400" style={{ left: "38%", top: 2 }} />
                  <span className="absolute w-1 h-1 rounded-full bg-amber-400" style={{ left: "50%", top: 2, transform: "translateX(-50%)" }} />
                  <span className="absolute w-1 h-1 rounded-full bg-amber-400" style={{ left: "62%", top: 2 }} />
                </span>
                Mid 30-70
              </span>
            </SelectItem>
            <SelectItem value="high" data-testid="seed-range-high">
              <span className="inline-flex items-center gap-2">
                <span data-testid="seed-range-dots-high" className="relative inline-block w-8 h-2" aria-hidden="true">
                  <span className="absolute w-1 h-1 rounded-full bg-[#00C805]" style={{ right: "24%", top: 2 }} />
                  <span className="absolute w-1 h-1 rounded-full bg-[#00C805]" style={{ right: "12%", top: 2 }} />
                  <span className="absolute w-1 h-1 rounded-full bg-[#00C805]" style={{ right: "0%", top: 2 }} />
                </span>
                High 70-100
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
        {parsedNums && (() => {
          const [lo, hi] = RANGE_BOUNDS[seedRandomRange] || RANGE_BOUNDS.spread;
          const inCount = countInRange(parsedNums, seedRandomRange);
          const all = inCount === parsedNums.length;
          const best = bestFitRangeKey(parsedNums);
          const bestCount = best ? countInRange(parsedNums, best) : 0;
          const wouldChange = best && best !== seedRandomRange;
          return (
            <span className="relative group inline-block">
              <button
                type="button"
                data-testid="seed-range-in-count-chip"
                data-in={inCount}
                data-total={parsedNums.length}
                onClick={() => {
                  if (!best) return;
                  if (best === seedRandomRange) {
                    toast(`Already on best-fit band: ${best}`, { duration: 2000 });
                    return;
                  }
                  setSeedRandomRange(best);
                  try { localStorage.setItem("stratos.seed-random-range", best); } catch {}
                  toast.success(`Range → ${best}`, {
                    description: `${bestCount}/${parsedNums.length} values fit that band best.`,
                    duration: 2500,
                  });
                }}
                className={
                  "mono text-[9px] tabular-nums px-1 py-[1px] rounded-sm border transition-colors cursor-pointer " +
                  (all
                    ? "border-[#00C805]/40 bg-[#00C805]/10 text-[#00C805] hover:bg-[#00C805]/20"
                    : "border-zinc-700 bg-transparent text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200")
                }
                title={`${inCount} of ${parsedNums.length} values inside ${seedRandomRange} band (${lo}-${hi}) · click to auto-pick best-fit`}
              >
                {inCount}/{parsedNums.length} in
              </button>
              {best && (
                <div
                  data-testid="seed-range-best-fit-tooltip"
                  data-best={best}
                  data-would-change={wouldChange ? "true" : "false"}
                  className="absolute right-0 top-full mt-1 z-50 hidden group-hover:block group-focus-within:block bg-zinc-950 border border-zinc-700 rounded-sm shadow-lg p-2 w-[220px] text-left normal-case tracking-normal"
                >
                  <div className="mono text-[9px] text-zinc-500 uppercase tracking-widest mb-1">
                    Click would pick
                  </div>
                  <div className="mono text-[10px] text-zinc-300 flex justify-between items-center">
                    <span
                      className={
                        "tabular-nums font-semibold " +
                        (best === "low" ? "text-red-300"
                          : best === "mid" ? "text-amber-300"
                          : best === "high" ? "text-[#00C805]"
                          : "text-zinc-400")
                      }
                    >
                      {best}
                    </span>
                    <span className="tabular-nums text-zinc-400">{bestCount}/{parsedNums.length} in</span>
                  </div>
                  <div data-testid="best-fit-sparkline" className="mt-2 space-y-1" data-sparkline-nav-root="true">
                    {["low", "mid", "high"].map((k) => {
                      const n = countInRange(parsedNums, k);
                      const pct = parsedNums.length ? (n / parsedNums.length) * 100 : 0;
                      const isWinner = k === best;
                      const barCls = k === "low" ? "bg-red-400" : k === "mid" ? "bg-amber-400" : "bg-[#00C805]";
                      const isCurrent = k === seedRandomRange;
                      return (
                        <button
                          type="button"
                          key={k}
                          data-testid={`best-fit-sparkline-bar-${k}`}
                          data-count={n}
                          data-winner={isWinner ? "true" : "false"}
                          data-current={isCurrent ? "true" : "false"}
                          data-sparkline-nav={k}
                          onMouseDown={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            // Iter 79 — Sparkline Keyboard Nav. Arrow
                            // left/right cycle through the 3 best-fit
                            // rows so keyboard-only users can jump
                            // bands without Tab. Home/End jump to the
                            // first/last row for symmetry.
                            if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
                            e.preventDefault();
                            const root = e.currentTarget.closest("[data-sparkline-nav-root]");
                            if (!root) return;
                            const btns = Array.from(root.querySelectorAll("[data-sparkline-nav]"));
                            const idx = btns.indexOf(e.currentTarget);
                            if (idx < 0) return;
                            let nextIdx = idx;
                            if (e.key === "ArrowLeft") nextIdx = (idx - 1 + btns.length) % btns.length;
                            else if (e.key === "ArrowRight") nextIdx = (idx + 1) % btns.length;
                            else if (e.key === "Home") nextIdx = 0;
                            else if (e.key === "End") nextIdx = btns.length - 1;
                            btns[nextIdx].focus();
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (isCurrent) {
                              toast(`Already on ${k}`, { duration: 1500 });
                              return;
                            }
                            setSeedRandomRange(k);
                            try { localStorage.setItem("stratos.seed-random-range", k); } catch {}
                            toast.success(`Range → ${k}`, {
                              description: `${n}/${parsedNums.length} values fit that band.`,
                              duration: 2500,
                            });
                          }}
                          className={
                            "w-full mono text-[9px] flex items-center gap-1.5 rounded-sm px-1 py-0.5 hover:bg-zinc-900 cursor-pointer text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 " +
                            (isCurrent
                              ? "ring-1 ring-amber-500/60 bg-amber-500/5 text-zinc-200"
                              : "text-zinc-500")
                          }
                          title={isCurrent ? `Currently on '${k}' band` : `Click to jump to '${k}' band`}
                        >
                          <span className="w-6 uppercase tracking-widest">{k}</span>
                          <span className="flex-1 h-1.5 bg-zinc-900 rounded-sm overflow-hidden">
                            <span
                              className={`block h-full ${barCls} ${isWinner ? "opacity-100" : "opacity-30"}`}
                              style={{ width: `${pct}%` }}
                            />
                          </span>
                          <span
                            className={
                              "tabular-nums w-4 text-right " +
                              (isWinner ? "text-zinc-200" : "text-zinc-500")
                            }
                          >
                            {n}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="mono text-[9px] text-zinc-600 uppercase tracking-widest mt-2 pt-1 border-t border-zinc-800">
                    {wouldChange ? "will switch on click" : "already on best-fit"}
                  </div>
                </div>
              )}
            </span>
          );
        })()}
        {parsedNums && (
          <span data-testid="seed-preview-chips" className="inline-flex items-center gap-0.5 ml-1">
            {parsedNums.map((v, i) => {
              const prev = i === 0 ? null : parsedNums[i - 1];
              let tone = "flat";
              let cls = "bg-zinc-700/60 text-zinc-300";
              if (prev != null) {
                if (v > prev) { tone = "up"; cls = "bg-[#00C805]/25 text-[#00C805]"; }
                else if (v < prev) { tone = "down"; cls = "bg-red-500/25 text-red-300"; }
              }
              const [lo, hi] = RANGE_BOUNDS[seedRandomRange] || RANGE_BOUNDS.spread;
              const inRange = v >= lo && v <= hi;
              const rangeDotCls = { spread: "bg-zinc-400", low: "bg-red-400", mid: "bg-amber-400", high: "bg-[#00C805]" }[seedRandomRange] || "bg-zinc-400";
              return (
                <span
                  key={`seed-chip-${i}`}
                  data-testid={`seed-preview-chip-${i}`}
                  data-tone={tone}
                  title={prev != null
                    ? `${v}% (${tone === "up" ? "▲" : tone === "down" ? "▼" : "▬"} vs ${prev}%) · ${inRange ? "in" : "out of"} ${seedRandomRange} range`
                    : `${v}% (start) · ${inRange ? "in" : "out of"} ${seedRandomRange} range`}
                  className={`mono text-[8px] tabular-nums px-1 py-[1px] rounded-sm inline-flex items-center gap-1 ${cls}`}
                >
                  <span
                    data-testid={`seed-chip-range-dot-${i}`}
                    data-range={seedRandomRange}
                    data-in-range={inRange ? "true" : "false"}
                    className={`w-1.5 h-1.5 rounded-full ${rangeDotCls} ${inRange ? "opacity-100" : "opacity-30"}`}
                    aria-hidden="true"
                  />
                  {v}
                </span>
              );
            })}
          </span>
        )}
        <button
          type="button"
          data-testid="seed-ctr-variance-btn"
          onClick={handleSeedClick}
          className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-amber-500/40 bg-amber-500/5 text-amber-300 hover:bg-amber-500/15 transition-colors"
          title="Insert synthetic snapshots for demo/screenshot purposes"
        >
          Seed variance
        </button>
      </div>
      {legendCounts && (() => {
        const { ups, downs, flats } = legendCounts;
        return (
          <div
            data-testid="seed-chip-legend"
            className="inline-flex items-center gap-2 mono text-[8px] uppercase tracking-widest text-zinc-500 pl-1"
          >
            <span className="inline-flex items-center gap-1" data-testid="seed-legend-up" data-count={ups} data-changed={legendChanged.ups ? "true" : "false"}>
              <span className="w-2 h-2 rounded-sm bg-[#00C805]/60" /> ▲ up · <span className={emphCls(legendChanged.ups)}>{ups}</span>
            </span>
            <span className="inline-flex items-center gap-1" data-testid="seed-legend-down" data-count={downs} data-changed={legendChanged.downs ? "true" : "false"}>
              <span className="w-2 h-2 rounded-sm bg-red-500/60" /> ▼ down · <span className={emphCls(legendChanged.downs)}>{downs}</span>
            </span>
            <span className="inline-flex items-center gap-1" data-testid="seed-legend-flat" data-count={flats} data-changed={legendChanged.flats ? "true" : "false"}>
              <span className="w-2 h-2 rounded-sm bg-zinc-500/60" /> ▬ flat/first · <span className={emphCls(legendChanged.flats)}>{flats}</span>
            </span>
          </div>
        );
      })()}
    </div>
  );
}
